import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Sparkles, Loader2, Send, X, MessageCircleQuestion, Mail } from 'lucide-react';
import { req } from '../services/api';


/**
 * Modal flotante para preguntar al buzón con Gemini.
 * Props (todas opcionales — si se pasan, acotan la búsqueda al contexto):
 *   - contactoEmail
 *   - entidadId
 *   - contextLabel (string para el header, ej. "Acme S.L." o "Juan Pérez")
 *   - trigger: ReactNode opcional para el botón de apertura. Si no se pasa,
 *              usa un botón estándar con icono.
 */
export default function PreguntarBuzon({ contactoEmail, entidadId, contextLabel, trigger, open: openProp, onOpenChange }) {
  const [openInner, setOpenInner] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : openInner;
  const setOpen = (v) => {
    if (isControlled) { onOpenChange?.(v); }
    else { setOpenInner(v); }
  };
  const [pregunta, setPregunta] = useState('');
  const [loading, setLoading] = useState(false);
  const [respuesta, setRespuesta] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const enviar = async () => {
    if (!pregunta.trim() || pregunta.trim().length < 3) {
      toast.error('Escribe una pregunta de al menos 3 caracteres');
      return;
    }
    setLoading(true);
    setRespuesta(null);
    try {
      const r = await req('/api/correo/ai/preguntar', {
        method: 'POST',
        body: JSON.stringify({
          pregunta: pregunta.trim(),
          contacto_email: contactoEmail || null,
          entidad_id: entidadId || null,
        }),
      });
      setRespuesta(r);
    } catch (e) {
      toast.error(`IA: ${e.message}`);
      setRespuesta({ respuesta: '', error: e.message });
    } finally { setLoading(false); }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <>
      {!isControlled && (
        trigger ? (
          <span onClick={() => setOpen(true)}>{trigger}</span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid="abrir-preguntar-buzon"
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
          >
            <MessageCircleQuestion className="w-4 h-4 mr-1.5" /> Preguntar al buzón
          </Button>
        )
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setOpen(false)}>
          <Card className="max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="dialog-preguntar-buzon">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MessageCircleQuestion className="w-5 h-5 text-violet-600" /> Preguntar al buzón
                  {contextLabel && (
                    <Badge className="bg-violet-50 text-violet-700 border-violet-200 border text-[10px]">
                      {contextLabel}
                    </Badge>
                  )}
                </h3>
                <p className="text-xs text-ink-500 mt-1">
                  {contactoEmail || entidadId
                    ? `Pregunta sobre los correos relacionados con ${contextLabel || 'este contexto'}.`
                    : 'Pregunta en lenguaje natural sobre tu propio buzón. Gemini solo accede a tus correos.'}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="cerrar-preguntar-buzon"><X className="w-4 h-4" /></Button>
            </div>

            <div className="flex gap-2 mt-3">
              <Input
                ref={inputRef}
                value={pregunta}
                onChange={(e) => setPregunta(e.target.value)}
                onKeyDown={handleKey}
                placeholder='Ej: "¿qué acordamos con esta empresa?", "¿hay algún correo pendiente de responder?"'
                data-testid="input-pregunta-buzon"
                disabled={loading}
              />
              <Button onClick={enviar} disabled={loading || !pregunta.trim()} className="bg-violet-600 hover:bg-violet-700" data-testid="enviar-pregunta">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>

            {loading && (
              <div className="py-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600 mx-auto mb-2" />
                <p className="text-sm text-ink-500">Gemini está leyendo tus correos…</p>
              </div>
            )}

            {!loading && respuesta && respuesta.respuesta && (
              <div className="mt-4 space-y-3" data-testid="respuesta-buzon">
                <div className="p-4 bg-violet-50 border border-violet-200 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-violet-600" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-violet-700">Respuesta IA</span>
                  </div>
                  <p className="text-sm text-ink-800 whitespace-pre-wrap">{respuesta.respuesta}</p>
                </div>

                {respuesta.correos_citados && respuesta.correos_citados.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-600 mb-1.5">
                      Correos citados ({respuesta.correos_citados.length})
                    </div>
                    <div className="space-y-1.5">
                      {respuesta.correos_citados.map((c, i) => (
                        <div key={i} className="p-2 border border-ink-200 rounded text-xs bg-white" data-testid={`correo-citado-${i}`}>
                          <div className="flex items-center gap-2 text-ink-500">
                            <Mail className="w-3 h-3" />
                            <span>{c.fecha || '—'}</span>
                            <span>·</span>
                            <span className="font-medium text-ink-700 truncate">{c.remitente}</span>
                          </div>
                          <div className="font-medium text-ink-900 mt-0.5">{c.asunto}</div>
                          {c.extracto && <div className="text-ink-600 italic mt-0.5">"{c.extracto}"</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-[10px] text-ink-400 text-right">
                  {respuesta.correos_consultados || 0} correos consultados · {respuesta.modelo}
                </div>
              </div>
            )}

            {!loading && respuesta && respuesta.error && (
              <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">
                Error: {respuesta.error}
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
